import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@ouropix/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Progress } from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import {
  CheckCircle, Eye, DollarSign, TrendingUp, Users,
  Copy, Pencil, Play, Award,
} from 'lucide-react';
import AdminPageHeader from '@/components/layout/PageHeader';
import { GameStatus } from '@/lib/game-enums';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api-helpers';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { GameDetailModal } from '@/components/games/GameDetailModal';
import { NewGameModal } from '@/components/games/NewGameModal';
import type { AdminGame } from '@/components/games/types';

export const Route = createFileRoute('/_admin/jogos/andamento')({
  component: JogosAndamentoPage,
});

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

interface GameGroup {
  key: string;
  structureId: string;
  label: string;
  lotepCode: string;
  poolTotal: number;
  poolConsumed: number;
  games: AdminGame[];
  revenue: number;
  prizesPaid: number;
}

function JogosAndamentoPage() {
  const queryClient = useQueryClient();
  const [gameToFinish, setGameToFinish] = useState<AdminGame | null>(null);
  const [gameToView, setGameToView] = useState<AdminGame | null>(null);
  const [gameToEdit, setGameToEdit] = useState<AdminGame | null>(null);

  const { data: games, isLoading } = useQuery<AdminGame[]>({
    queryKey: ['admin-games-active'],
    queryFn: async () => {
      // /admin/games traz o objeto completo do jogo (usado por editar/duplicar/
      // detalhes). /admin/games/active-stats traz stats reais (receita, prêmios
      // pagos, jogadores únicos) + a pool LOTEP compartilhada. Fazemos merge por
      // id: se o stats falhar, a página continua funcionando com zeros.
      const [gamesRes, statsRes] = await Promise.all([
        api.get('/admin/games', { params: { status: 'active', limit: 200 } }),
        api.get('/admin/games/active-stats', { params: { limit: 200 } }).catch(() => null),
      ]);
      const raw: any[] = gamesRes.data?.data ?? gamesRes.data?.games ?? [];
      const statsList: any[] = statsRes?.data?.games ?? [];
      const statsById = new Map(statsList.map((s: any) => [s.id, s]));
      return raw.map((g: any) => {
        const s: any = statsById.get(g.id) ?? {};
        return {
          ...g,
          // Receita computada do game-catalog (used_plays×preço) — confiável;
          // o active-stats do admin-svc lê colunas zeradas de `games`.
          actual_revenue: (g.used_plays || 0) * (Number(g.play_price) || 0),
          prizes_paid: s.prizes_paid ?? null,
          estimated_profit: s.estimated_profit ?? null,
          unique_players: s.unique_players ?? 0,
          use_lotep_pool: s.use_lotep_pool ?? g.use_lotep_pool ?? false,
          // Pool COMPARTILHADA da estrutura — vem do game-catalog (confiável);
          // o active-stats do admin-svc é stale. NÃO é uma pool por-jogo.
          pool_numbers_total: (g.pool_numbers_total ?? 0) || (s.pool_numbers_total ?? 0),
          pool_numbers_consumed: (g.pool_numbers_consumed ?? 0) || (s.pool_numbers_consumed ?? 0),
          // Estrutura vem do game-catalog (confiável); o active-stats do admin-svc
          // devolve vazio porque o banco de jogos dele é stale. Fallback pro stats.
          structure_id: g.lotep_structure_id || s.structure_id || '',
          campaign_name: g.campaign_name || s.campaign_name || '',
          lotep_code: g.lotep_code || s.lotep_code || '',
        };
      }) as AdminGame[];
    },
    refetchInterval: 10000,
  });

  const finishGameMutation = useMutation({
    mutationFn: async (gameId: string) => {
      await api.patch(`/admin/games/${gameId}`, {
        status: 'completed' as GameStatus,
        end_at: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games-active'] });
      queryClient.invalidateQueries({ queryKey: ['admin-games-completed'] });
      toast.success('Jogo encerrado com sucesso!');
      setGameToFinish(null);
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Erro ao encerrar jogo'));
    },
  });

  const duplicateGameMutation = useMutation({
    mutationFn: async (game: AdminGame) => {
      const r = await api.post('/admin/games', {
        title: `${game.title} (Copia)`,
        description: game.description,
        modality_id: game.modality_id,
        theme_id: game.theme_id,
        play_price: game.play_price,
        total_plays: game.total_plays,
        featured: game.featured,
        status: 'draft',
      });
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games-registered'] });
      toast.success('Jogo duplicado como rascunho!');
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Erro ao duplicar jogo'));
    },
  });

  const allGames = games ?? [];
  const totalRevenue = allGames.reduce((sum, g) => sum + ((g as any).actual_revenue ?? (g.used_plays || 0) * (Number(g.play_price) || 0)), 0);
  const totalPlays = allGames.reduce((sum, g) => sum + (g.used_plays || 0), 0);
  const totalPrizesPaid = allGames.reduce((sum, g) => sum + ((g as any).prizes_paid ?? 0), 0);
  const totalUniquePlayers = allGames.reduce((sum, g) => sum + ((g as any).unique_players ?? 0), 0);

  // Agrupa os jogos por estrutura LOTEP (jogos da mesma estrutura dividem 1 pool).
  // Cada grupo vira uma tabela com o progresso da pool no cabeçalho (uma vez, sem
  // duplicar por linha). Jogos sem estrutura caem num grupo "Sem estrutura".
  const groups = (() => {
    const map = new Map<string, GameGroup>();
    for (const g of allGames) {
      const sid = ((g as any).structure_id as string) || '';
      const key = sid || '__none__';
      let grp = map.get(key);
      if (!grp) {
        grp = {
          key,
          structureId: sid,
          label: sid ? ((g as any).campaign_name || (g as any).lotep_code || 'Estrutura LOTEP') : 'Sem estrutura',
          lotepCode: ((g as any).lotep_code as string) || '',
          poolTotal: Number((g as any).pool_numbers_total ?? 0),
          poolConsumed: Number((g as any).pool_numbers_consumed ?? 0),
          games: [],
          revenue: 0,
          prizesPaid: 0,
        };
        map.set(key, grp);
      }
      grp.games.push(g);
      grp.revenue += Number((g as any).actual_revenue ?? 0);
      grp.prizesPaid += Number((g as any).prizes_paid ?? 0);
      // Pool é COMPARTILHADA: todos os jogos da estrutura têm o mesmo número.
      // Usa o maior (não soma!) pra não inflar e ser robusto a link faltando.
      grp.poolTotal = Math.max(grp.poolTotal, Number((g as any).pool_numbers_total ?? 0));
      grp.poolConsumed = Math.max(grp.poolConsumed, Number((g as any).pool_numbers_consumed ?? 0));
    }
    return [...map.values()].sort((a, b) => {
      if (a.key === '__none__') return 1;
      if (b.key === '__none__') return -1;
      return a.label.localeCompare(b.label);
    });
  })();

  // Card de jogo (bento). Em grupo de estrutura (inStructure), o progresso da pool
  // vive no cabeçalho do grupo — o card mostra só as jogadas do próprio jogo. Fora
  // de estrutura, o card mostra a barra de progresso individual.
  const renderGameCard = (game: AdminGame, inStructure: boolean) => {
    const revenue = (game as any).actual_revenue ?? ((game.used_plays || 0) * (Number(game.play_price) || 0));
    const prizesPaid: number | null = (game as any).prizes_paid != null ? Number((game as any).prizes_paid) : null;
    const estimatedProfit: number | null = (game as any).estimated_profit != null ? Number((game as any).estimated_profit) : null;
    const progress = game.total_plays > 0 ? (game.used_plays / game.total_plays) * 100 : 0;
    return (
      <Card key={game.id} className="flex flex-col">
        <CardContent className="p-4 flex flex-col gap-3 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              {(game.theme?.theme_assets?.avatar_image_url ?? game.icon_url) && (
                <img src={game.theme?.theme_assets?.avatar_image_url ?? game.icon_url ?? ''} alt={`Ícone ${game.title}`} className="h-11 w-11 rounded-lg object-cover shrink-0" loading="lazy" />
              )}
              <div className="min-w-0">
                <p className="font-semibold truncate">{game.title}</p>
                <p className="text-xs text-muted-foreground truncate">{game.theme?.name || '-'}</p>
              </div>
            </div>
            <Badge variant="outline" className="shrink-0">{game.modality?.name || '-'}</Badge>
          </div>

          {inStructure ? (
            <p className="text-xs text-muted-foreground">
              {(game.used_plays || 0).toLocaleString('pt-BR')} jogadas
            </p>
          ) : (
            <div className="space-y-1">
              <Progress value={progress} className="h-2" />
              <span className="text-xs text-muted-foreground">
                {(game.used_plays || 0).toLocaleString('pt-BR')}/{game.total_plays.toLocaleString('pt-BR')} ({progress.toFixed(1)}%)
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border p-2">
              <p className="text-xs text-muted-foreground">Receita</p>
              <p className="text-lg font-semibold text-green-500">{formatCurrency(revenue)}</p>
            </div>
            <div className="rounded-lg border p-2">
              <p className="text-xs text-muted-foreground">Prêmios pagos</p>
              <p className="text-lg font-semibold text-amber-500">{prizesPaid !== null ? formatCurrency(prizesPaid) : '—'}</p>
            </div>
            <div className="rounded-lg border p-2">
              <p className="text-xs text-muted-foreground">Lucro est.</p>
              <p className={`text-lg font-semibold ${estimatedProfit !== null ? (estimatedProfit >= 0 ? 'text-green-500' : 'text-red-500') : ''}`}>
                {estimatedProfit !== null ? formatCurrency(estimatedProfit) : '—'}
              </p>
            </div>
            <div className="rounded-lg border p-2">
              <p className="text-xs text-muted-foreground">Iniciado em</p>
              <p className="text-sm font-medium">{game.start_at ? format(new Date(game.start_at), 'dd/MM/yy HH:mm', { locale: ptBR }) : '-'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-auto pt-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => setGameToView(game)} title="Ver detalhes">
              <Eye className="mr-2 h-4 w-4" />
              Detalhes
            </Button>
            <Button variant="outline" size="icon" onClick={() => setGameToEdit(game)} title="Editar conteúdo" aria-label="Editar">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => duplicateGameMutation.mutate(game)}
              disabled={duplicateGameMutation.isPending}
              title="Duplicar jogo"
              aria-label="Duplicar jogo"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={() => setGameToFinish(game)} title="Encerrar jogo" aria-label="Encerrar jogo">
              <CheckCircle className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Play}
        title="Jogos em Andamento"
        description="Jogos ativos - monitore vendas e métricas em tempo real"
      />

      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Jogos Ativos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{allGames.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <DollarSign className="h-4 w-4" />Receita Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{formatCurrency(totalRevenue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Award className="h-4 w-4" />Prêmios Pagos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">{formatCurrency(totalPrizesPaid)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Users className="h-4 w-4" />Jogadores Únicos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{totalUniquePlayers.toLocaleString('pt-BR')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-4 w-4" />Total Jogadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalPlays.toLocaleString('pt-BR')}</div>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center">Carregando...</CardContent>
        </Card>
      ) : allGames.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Nenhum jogo em andamento no momento.
          </CardContent>
        </Card>
      ) : (
        groups.map(group => {
          const inStructure = !!group.structureId;
          const poolPct = group.poolTotal > 0 ? (group.poolConsumed / group.poolTotal) * 100 : 0;
          return (
            <Card key={group.key}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">{group.label}</CardTitle>
                    {inStructure && group.lotepCode && (
                      <p className="text-xs text-muted-foreground font-mono">{group.lotepCode}</p>
                    )}
                  </div>
                  {inStructure && group.poolTotal > 0 && (
                    <div className="min-w-[200px] space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Pool compartilhada</span>
                        <span>
                          {group.poolConsumed.toLocaleString('pt-BR')}/{group.poolTotal.toLocaleString('pt-BR')} ({poolPct.toFixed(1)}%)
                        </span>
                      </div>
                      <Progress value={poolPct} className="h-2" />
                    </div>
                  )}
                  <div className="flex gap-4 text-xs">
                    <div>
                      <span className="text-muted-foreground">Receita </span>
                      <span className="text-green-500 font-medium">{formatCurrency(group.revenue)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Prêmios </span>
                      <span className="text-amber-500 font-medium">{formatCurrency(group.prizesPaid)}</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {group.games.map(game => renderGameCard(game, inStructure))}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      <NewGameModal
        open={!!gameToEdit}
        onOpenChange={open => { if (!open) setGameToEdit(null); }}
        game={gameToEdit ?? undefined}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['admin-games-active'] })}
      />

      {gameToView && (
        <GameDetailModal
          open={!!gameToView}
          onOpenChange={() => setGameToView(null)}
          game={gameToView}
        />
      )}

      <ConfirmModal
        isOpen={!!gameToFinish}
        onClose={() => setGameToFinish(null)}
        onConfirm={() => { if (gameToFinish) finishGameMutation.mutate(gameToFinish.id); }}
        title="Encerrar Jogo"
        description={
          <div className="space-y-2">
            <p>Tem certeza que deseja encerrar o jogo "{gameToFinish?.title}"? Jogadas restantes não poderão mais ser vendidas.</p>
            {gameToFinish && (gameToFinish.total_plays - (gameToFinish.used_plays || 0)) > 0 && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400">
                <p className="font-medium">Atenção: Jogadas Pendentes</p>
                <p className="text-sm mt-1">
                  Este jogo ainda tem {(gameToFinish.total_plays - (gameToFinish.used_plays || 0)).toLocaleString('pt-BR')} jogadas disponíveis para venda.
                </p>
              </div>
            )}
          </div>
        }
        confirmText={(gameToFinish?.total_plays ?? 0) - (gameToFinish?.used_plays ?? 0) > 0 ? 'Encerrar Mesmo Assim' : 'Encerrar Jogo'}
        cancelText="Cancelar"
      />
    </div>
  );
}
